/* eslint-disable no-unused-vars */
import { IdefaultExpandedRowContext } from "@/components/widgets/tradeActionSummary/ActionSummaryTableHelperFunctions";
import { create } from "zustand";

// Store structure for expanded rows
type ExpandedRowsData = {
  [cryptoID: string]: IdefaultExpandedRowContext[];
};

// Store structure for most recently updated tracking
type MostRecentlyUpdatedData = {
  [cryptoID: string]: string;
};

type ExpandedRowsActions = {
  // Get expanded rows for a specific widget
  getExpandedRows: (cryptoID: string) => IdefaultExpandedRowContext[];

  // Set expanded rows for a specific widget
  setExpandedRows: (
    cryptoID: string,
    rows: IdefaultExpandedRowContext[],
  ) => void;

  // Update a single row's expanded state for a specific widget
  updateExpandedRow: (
    cryptoID: string,
    esid: string,
    updates: Partial<IdefaultExpandedRowContext>,
  ) => void;

  // Clear expanded rows for a specific widget (useful for cleanup)
  clearExpandedRows: (cryptoID: string) => void;

  // Get the most recently updated esid for a specific widget
  getMostRecentlyUpdated: (cryptoID: string) => string;

  // Set the most recently updated esid for a specific widget
  setMostRecentlyUpdated: (cryptoID: string, esid: string) => void;
};

type State = {
  // Expanded rows data indexed by cryptoID
  expandedRowsData: ExpandedRowsData;
  // Track most recently updated esid per widget
  mostRecentlyUpdated: MostRecentlyUpdatedData;
} & ExpandedRowsActions;

const expandedRowsStore = create<State>((set, get) => ({
  // Initial state - empty objects
  expandedRowsData: {},
  mostRecentlyUpdated: {},

  // Get expanded rows for a specific widget
  getExpandedRows: (cryptoID: string) => {
    const state = get();
    return state.expandedRowsData[cryptoID] || [];
  },

  // Set expanded rows for a specific widget
  setExpandedRows: (cryptoID: string, rows: IdefaultExpandedRowContext[]) => {
    set((state) => ({
      ...state,
      expandedRowsData: {
        ...state.expandedRowsData,
        [cryptoID]: rows,
      },
    }));
  },

  // Update a single row's expanded state
  updateExpandedRow: (
    cryptoID: string,
    esid: string,
    updates: Partial<IdefaultExpandedRowContext>,
  ) => {
    set((state) => {
      const currentRows = state.expandedRowsData[cryptoID] || [];
      const existingRow = currentRows.find((row) => row.esid === esid);

      let newRows: IdefaultExpandedRowContext[];
      if (existingRow) {
        // Update existing row
        newRows = currentRows.map((row) =>
          row.esid === esid ? { ...row, ...updates } : row,
        );
      } else {
        // Add new row
        newRows = [
          ...currentRows,
          {
            esid,
            allocationsShowing: false,
            legsShowing: false,
            editPanelShowing: false,
            ...updates,
          },
        ];
      }

      return {
        ...state,
        expandedRowsData: {
          ...state.expandedRowsData,
          [cryptoID]: newRows,
        },
      };
    });
  },

  // Clear expanded rows for a specific widget
  clearExpandedRows: (cryptoID: string) => {
    set((state) => {
      const newExpandedRowsData = { ...state.expandedRowsData };
      delete newExpandedRowsData[cryptoID];
      return {
        ...state,
        expandedRowsData: newExpandedRowsData,
      };
    });
  },

  // Get most recently updated esid
  getMostRecentlyUpdated: (cryptoID: string) => {
    const state = get();
    return state.mostRecentlyUpdated[cryptoID] || "";
  },

  // Set most recently updated esid
  setMostRecentlyUpdated: (cryptoID: string, esid: string) => {
    set((state) => ({
      ...state,
      mostRecentlyUpdated: {
        ...state.mostRecentlyUpdated,
        [cryptoID]: esid,
      },
    }));
  },
}));

export default expandedRowsStore;
